import axios from 'axios';

const getCsrfToken = () => {
  const name = 'csrftoken';
  const cookieValue = document.cookie.split('; ').find(row => row.startsWith(name))?.split('=')[1];
  return cookieValue;
};

// URL única para todos los entornos. Funciona en desarrollo gracias al `proxy`
// de package.json (:3000 reenvía a Django en :8000) y en producción porque
// Django sirve el build de React y el API en el mismo origen.
const API_BASE_URL = '/api';

// Crea una instancia de Axios con configuración global
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de request: se ejecuta antes de cada petición para agregar los headers
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    config.headers['X-CSRFToken'] = getCsrfToken();
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response para manejar errores 401 y refrescar el token
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh');
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${API_BASE_URL}/users/token/refresh`,
            { refresh: refreshToken },
            {
              headers: {
                'X-CSRFToken': getCsrfToken(),
              },
              withCredentials: true,
            }
          );
          localStorage.setItem('access', data.access);
          if (data.refresh) {
            localStorage.setItem('refresh', data.refresh);
          }
          originalRequest.headers['Authorization'] = `Bearer ${data.access}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// --- Funciones de la API ---

export const ensureCsrfToken = async () => {
  await apiClient.get('/users/csrf');
};

// Funciones para el usuario

export const registerUser = async (payload) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/register', payload);
  return response.data;
};

export const loginUser = async (document, password) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/login', {
    document,
    password,
  });
  if (response.data.access) {
    localStorage.setItem('access', response.data.access);
  }
  if (response.data.refresh) {
    localStorage.setItem('refresh', response.data.refresh);
  }
  return response.data;
};

export const logoutUser = async (refreshToken, accessToken) => {
  await apiClient.post('/users/logout', { refresh: refreshToken }, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const getUserDetails = async (token) => {
  const response = await apiClient.get('/users/info', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.data;
};

export const getUserProfile = async (token) => {
  const response = await apiClient.get('/users/profile', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.data;
};

export const updateUserProfile = async (token, formData) => {
  try {
    const response = await apiClient.patch('/users/profile', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendPasswordResetEmail = async (email) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/reset-password', {
    email,
  });
  return response.data;
};

export const confirmPasswordReset = async (uidb64, token, newPassword) => {
  await ensureCsrfToken();
  const response = await apiClient.post('/users/reset-password/confirm', {
    uidb64,
    token,
    new_password: newPassword,
  });
  return response.data;
};

export const changePassword = async (token, currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/users/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ── BMC singleton ─────────────────────────────────────────────────────────

export const getBmcQuestions = async () => {
  const { data } = await apiClient.get('/bmc/questions');
  return data;
};

export const getBmcGuideTips = async (blockId) => {
  const { data } = await apiClient.get('/bmc/guide-tips', { params: { block: blockId } });
  return data;
};

export const getBmcMe = async () => {
  const { data } = await apiClient.get('/bmc/me');
  return data;
};

export const saveBmcMe = async (payload) => {
  const { data } = await apiClient.patch('/bmc/me', payload);
  return data;
};

export const generateBmcMe = async (payload) => {
  const { data } = await apiClient.post('/bmc/me/generate', payload);
  return data;
};

// ── Pitch singleton ───────────────────────────────────────────────────────

export const getPitchQuestions = async () => {
  const { data } = await apiClient.get('/pitch/questions');
  return data;
};

export const getPitchGuideTips = async (section, audience) => {
  const { data } = await apiClient.get('/pitch/guide-tips', {
    params: { section, audience },
  });
  return data;
};

export const getPitchMe = async () => {
  const { data } = await apiClient.get('/pitch/me');
  return data;
};

export const savePitchMe = async (payload) => {
  const { data } = await apiClient.patch('/pitch/me', payload);
  return data;
};

export const generatePitchMe = async (payload) => {
  const { data } = await apiClient.post('/pitch/me/generate', payload);
  return data;
};

// ── PMV singleton ─────────────────────────────────────────────────────────

export const getPmvQuestions = async () => {
  const { data } = await apiClient.get('/pmv/questions');
  return data;
};

export const getPmvGuideTips = async (section) => {
  const { data } = await apiClient.get('/pmv/guide-tips', { params: { section } });
  return data;
};

export const getPmvMe = async () => {
  const { data } = await apiClient.get('/pmv/me');
  return data;
};

export const savePmvMe = async (payload) => {
  const { data } = await apiClient.patch('/pmv/me', payload);
  return data;
};

export const generatePmvMe = async (payload) => {
  const { data } = await apiClient.post('/pmv/me/generate', payload);
  return data;
};

// ── Oportunidades singleton ───────────────────────────────────────────────

export const getOppQuestions = async () => {
  const { data } = await apiClient.get('/opp/questions');
  return data;
};

export const getOppGuideTips = async (blockId) => {
  const { data } = await apiClient.get('/opp/guide-tips', { params: { block: blockId } });
  return data;
};

export const getOppMe = async () => {
  const { data } = await apiClient.get('/opp/me');
  return data;
};

export const saveOppMe = async (payload) => {
  const { data } = await apiClient.patch('/opp/me', payload);
  return data;
};

export const generateOppMe = async (payload) => {
  const { data } = await apiClient.post('/opp/me/generate', payload);
  return data;
};

// ── Resumen de progreso de herramientas ───────────────────────────────────

export const getToolsSummary = async () => {
  const { data } = await apiClient.get('/tools/summary');
  return data;
};

// Perfil Profesional (único por usuario, desacoplado de BusinessPlan)

export const getPerfilQuestions = async () => {
  const { data } = await apiClient.get('/perfil/questions');
  return data;
};

export const getPerfilGuideTips = async (section) => {
  const { data } = await apiClient.get('/perfil/guide-tips', { params: { section } });
  return data;
};

export const getMyPerfil = async () => {
  const { data } = await apiClient.get('/perfil/me');
  return data;
};

export const saveMyPerfil = async (payload) => {
  const { data } = await apiClient.patch('/perfil/me', payload);
  return data;
};

export const generateMyPerfil = async (payload) => {
  const { data } = await apiClient.post('/perfil/me/generate', payload);
  return data;
};

export default apiClient;